import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Edit, Trash2, Link2, Search } from 'lucide-react'
import { knowledgeApiClient } from '@/lib/api'
import type { KnowledgeBase, KnowledgeBaseWithLinks } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { getDomain } from '@super/shared-types'
import { toast } from 'sonner'
import Fuse from 'fuse.js'
import { isMobileBreakpoint, confirmDialog } from '@/lib/utils/window-utils'

interface KnowledgeBasesTabProps {
  knowledgeBases: KnowledgeBaseWithLinks[]
  loading: boolean
  onEdit: (kb: KnowledgeBase) => void
  onLinkEdit: (url: string) => void
  onReload: () => void
}

export function KnowledgeBasesTab({
  knowledgeBases,
  loading,
  onEdit,
  onLinkEdit,
  onReload,
}: KnowledgeBasesTabProps) {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [searchQuery, setSearchQuery] = useState('')

  const handleDelete = async (kb: KnowledgeBase) => {
    if (
      !confirmDialog('Are you sure you want to delete this knowledge base?')
    ) {
      return
    }

    if (!currentCompany?.id) return

    try {
      if (isSuperAdmin) {
        await knowledgeApiClient.deleteKnowledgeBaseForCompany(
          kb.id,
          currentCompany.id,
        )
      } else {
        await knowledgeApiClient.deleteKnowledgeBase(kb.id)
      }
      toast.success('Knowledge base deleted successfully')
      onReload()
    } catch (error) {
      console.error('Error deleting knowledge base:', error)
      toast.error('Failed to delete knowledge base')
    }
  }

  const fuse = useMemo(
    () =>
      new Fuse(knowledgeBases, {
        keys: ['title', 'body'],
        threshold: 0.1,
        includeScore: true,
        ignoreLocation: true,
        findAllMatches: true,
      }),
    [knowledgeBases],
  )

  const filteredKnowledgeBases = useMemo(() => {
    if (!searchQuery.trim()) {
      return knowledgeBases
    }

    const query = searchQuery.toLowerCase()

    const exactMatches = knowledgeBases.filter(
      kb =>
        kb.title.toLowerCase().includes(query) ||
        kb.body.toLowerCase().includes(query),
    )

    const fuzzyResults = fuse.search(searchQuery)
    const fuzzyMatches = fuzzyResults
      .map(result => result.item)
      .filter(item => !exactMatches.some(exact => exact.id === item.id))

    return [...exactMatches, ...fuzzyMatches]
  }, [knowledgeBases, searchQuery, fuse])

  return (
    <div className="space-y-4">
      <div className="relative max-w-96">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search knowledge bases..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {loading ? (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Knowledge base</TableHead>
                <TableHead className="w-[240px] hidden lg:table-cell">
                  Links
                </TableHead>
                <TableHead
                  className="w-[100px] hidden lg:table-cell"
                  align="center"
                >
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-64" />
                      <div className="lg:hidden">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : filteredKnowledgeBases.length === 0 ? (
        <div className="border rounded-lg p-8">
          <p className="text-sm text-muted-foreground text-center">
            {searchQuery
              ? 'No knowledge bases found matching your search.'
              : 'No knowledge bases yet. Create your first one to get started.'}
          </p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Knowledge base</TableHead>
                <TableHead className="w-[240px] hidden lg:table-cell">
                  Links
                </TableHead>
                <TableHead
                  className="w-[100px] hidden lg:table-cell"
                  align="center"
                >
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredKnowledgeBases.map(kb => (
                <TableRow
                  key={kb.id}
                  className="lg:cursor-default cursor-pointer lg:hover:bg-transparent hover:bg-muted/50"
                  onClick={() => {
                    // Only handle click on mobile/tablet (when actions column is hidden)
                    if (isMobileBreakpoint()) {
                      onEdit(kb)
                    }
                  }}
                >
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{kb.title}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-md">
                        {kb.body.substring(0, 150)}
                        {kb.body.length > 150 && '...'}
                      </div>
                      {/* Show links on mobile/tablet */}
                      <div className="lg:hidden">
                        {kb.links && kb.links.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {kb.links.slice(0, 2).map(kbl => (
                              <Badge
                                key={kbl.id}
                                variant="outline"
                                className="text-xs cursor-pointer hover:bg-accent"
                                onClick={e => {
                                  e.stopPropagation()
                                  onLinkEdit(kbl.link.url)
                                }}
                              >
                                <Link2 className="h-3 w-3 mr-1" />
                                {getDomain(kbl.link.url)}
                              </Badge>
                            ))}
                            {kb.links.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{kb.links.length - 2} more
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground mt-1 block">
                            No links
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {kb.links && kb.links.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {kb.links.slice(0, 2).map(kbl => (
                          <TooltipProvider key={kbl.id}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge
                                  variant="outline"
                                  className="text-xs cursor-pointer hover:bg-accent"
                                  onClick={() => onLinkEdit(kbl.link.url)}
                                >
                                  <Link2 className="h-3 w-3 mr-1" />
                                  {getDomain(kbl.link.url)}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{kbl.link.url}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ))}
                        {kb.links.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{kb.links.length - 2} more
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        No links
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(kb)}
                        data-testid={`edit-kb-${kb.id}`}
                        aria-label={`Edit ${kb.title}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(kb)}
                        data-testid={`delete-kb-${kb.id}`}
                        aria-label={`Delete ${kb.title}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
